import { EVENT_SOURCE_META } from "@/components/restaurant/finance/constants";

// Čista logika Finansija (bez Vue-a i bez mreže): iznosi, raspodjela prometa, obračuni i procjena perioda,
// grupisanje transakcija i izvod. Isti kod koristi i ljuska i test u Node-u.

export const r2 = (value) => Math.round((Number(value) || 0) * 100) / 100;
export const toNumber = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);

/** "1.264,50" / "1264,50" / "1264.50" -> 1264.5 (polje za iznos prihvata zarez) */
export const parseAmount = (value) => {
  const text = String(value === null || value === undefined ? "" : value).trim().replace(/\s/g, "");
  if (!text) return 0;
  const normalized = text.includes(",") && text.includes(".") ? text.replace(/\./g, "").replace(",", ".") : text.replace(",", ".");
  const number = Number(normalized);
  return Number.isFinite(number) ? number : 0;
};

export const localIso = (date = new Date()) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`;
};

export const addDays = (iso, days) => {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return localIso(new Date(y, m - 1, d + days));
};

export const firstOfMonth = (iso) => `${iso.slice(0, 7)}-01`;

/** Udio prometa koji ostaje restoranu (0–100); null kad nema smisla (nema prometa ili je neto <= 0). */
export const shareOfTurnover = (net, turnover) => {
  if (!(turnover > 0) || !(net > 0)) return null;
  return Math.max(0, Math.min(100, Math.round((net / turnover) * 100)));
};

// ------------------------------------------------------------------ obračuni

/** "DD.MM.YYYY - DD.MM.YYYY" (stariji zapisi) -> { from, to } ISO; inače prazno. */
const PERIOD_RE = /(\d{1,2})\.(\d{1,2})\.(\d{4})\.?\s*[-–]\s*(\d{1,2})\.(\d{1,2})\.(\d{4})/;
const iso = (d, m, y) => `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

export const parsePeriod = (dto) => {
  if (dto.period_from && dto.period_to) {
    return { from: String(dto.period_from).slice(0, 10), to: String(dto.period_to).slice(0, 10) };
  }
  const match = PERIOD_RE.exec(dto.period || "");
  return match ? { from: iso(match[1], match[2], match[3]), to: iso(match[4], match[5], match[6]) } : {};
};

const createdBy = (dto) => {
  const by = dto.created_by_name || dto.created_by || dto.recorded_by;
  return typeof by === "object" && by ? by.name : by || "";
};

/** GET /payouts (iznosi su u KM): jedna isplata = jedan obračun sa plaćenim i otvorenim iznosom. */
export const mapPayout = (dto) => {
  const net = toNumber(dto.net);
  const paid = dto.paid_amount !== null && dto.paid_amount !== undefined ? toNumber(dto.paid_amount) : dto.status === "paid" ? net : 0;
  const remaining = dto.remaining !== null && dto.remaining !== undefined ? toNumber(dto.remaining) : Math.max(r2(net - paid), 0);
  return {
    key: `payout-${dto.id}`,
    kind: "payout",
    date: dto.date || "",
    periodLabel: dto.period || "",
    ...parsePeriod(dto),
    turnover: toNumber(dto.turnover),
    deductions: toNumber(dto.deductions),
    net,
    paid,
    remaining,
    status: dto.status,
    method: dto.method || "",
    reference: dto.reference || "",
    note: dto.note || "",
    createdBy: createdBy(dto),
  };
};

/** GET /platform-payments -> data.payments[]: uplata restorana Orderi (nema perioda ni neto obračuna). */
export const mapPayment = (dto) => {
  const amount = toNumber(dto.amount);
  return {
    key: `payment-${dto.id}`,
    kind: "payment",
    date: dto.date || dto.created_at || "",
    periodLabel: dto.period || "",
    ...parsePeriod(dto),
    turnover: null,
    deductions: null,
    net: -amount,
    paid: amount,
    remaining: 0,
    status: "received",
    method: dto.method || "",
    reference: dto.reference || "",
    note: dto.note || "",
    createdBy: createdBy(dto),
  };
};

/** estimate | received | paid | partial | pending */
export const settlementStatus = (row) => {
  if (row.kind === "estimate") return "estimate";
  if (row.kind === "payment") return "received";
  if (row.status === "paid" && row.remaining <= 0.004) return "paid";
  if (row.paid > 0.004 && row.remaining > 0.004) return "partial";
  return row.status === "paid" ? "paid" : "pending";
};

/** Procenat plaćenog dijela obračuna (0–100). */
export const paidPercent = (row) => (row.net > 0 ? Math.max(0, Math.min(100, Math.round((row.paid / row.net) * 100))) : 0);

/** Zbir otvorenog iznosa zaključenih obračuna (uplate restorana i procjena ne ulaze). */
export const openFromPrevious = (rows) =>
  r2(rows.filter((row) => row.kind === "payout" && row.remaining > 0.004).reduce((sum, row) => sum + row.remaining, 0));

/**
 * Period procjene: od dana poslije zadnjeg obračunatog perioda (ili od prvog u mjesecu kad nijedan zapis nema
 * datume) do danas. null kad je taj period prazan.
 */
export const estimateRange = (payouts, today = localIso()) => {
  const ends = payouts.map((row) => row.to).filter(Boolean).sort();
  const from = ends.length ? addDays(ends[ends.length - 1], 1) : firstOfMonth(today);
  return from <= today ? { from, to: today } : null;
};

/** Red "Tekući period (procjena)" iz financial-overview za period procjene; null kad nema aktivnosti. */
export const buildEstimate = (overview, range) => {
  if (!range || !overview) return null;
  const turnover = overview.turnover || 0;
  if (!(turnover > 0) && !overview.rows.length) return null;
  return {
    key: "estimate",
    kind: "estimate",
    date: range.to,
    periodLabel: "",
    from: range.from,
    to: range.to,
    turnover,
    deductions: overview.deductions === null || overview.deductions === undefined ? 0 : r2(overview.deductions),
    net: overview.net === null || overview.net === undefined ? 0 : r2(overview.net),
    paid: 0,
    remaining: 0,
    status: "estimate",
    method: "",
    reference: "",
    note: "",
    createdBy: "",
    overview,
  };
};

/** Procjena na vrhu, ostalo od najnovijeg ka starijem po datumu. */
export const settlementRows = (payouts, payments, estimate) => {
  const closed = [...payouts, ...payments].sort((a, b) => String(b.date).localeCompare(String(a.date)));
  return estimate ? [estimate, ...closed] : closed;
};

// ------------------------------------------------------------------ transakcije

const isRefund = (entryType) => /REFUND/.test(entryType || "");
const isCash = (entryType) => entryType === "CASH_ADVANCE" || entryType === "CASH_RECEIVED";
export const PAYMENT_SOURCES = ["RESTAURANT_PAYOUT", "PAYOUT", "PAYMENT_RECEIVED"];

/** GET /transactions: jedan red knjige. Iznos je apsolutan, smjer je u direction. */
export const mapTransaction = (dto) => ({
  id: dto.id,
  transactionId: dto.transaction_id === undefined ? null : dto.transaction_id,
  entryType: dto.entry_type || "",
  direction: dto.direction,
  amount: Math.abs(toNumber(dto.amount)),
  moneyFlow: dto.money_flow || null,
  sourceType: dto.source_type || "",
  sourceId: dto.source_id === undefined ? null : dto.source_id,
  rateType: dto.rate_type === undefined ? null : dto.rate_type,
  rateValue: dto.rate_value !== null && dto.rate_value !== undefined ? Number(dto.rate_value) : null,
  serviceName: dto.service_name || dto.service || "",
  serviceCode: dto.service_code || "",
  // "CASH" | "CARD" | "" (samo za narudžbe)
  paymentMethod: String(dto.payment_method || "").toUpperCase(),
  note: dto.note || "",
  date: dto.created_at || "",
});

/**
 * Kartice transakcija. Narudžba (source_type=ORDER) je jedna kartica po source_id (backend je razdvaja na više
 * transaction_id: prijem keša, dostava, storno); ostali događaji (isplata, uplata, pretplata, storno...) su po
 * transaction_id svaki svoja kartica.
 * Ton: in (u korist restorana), out (na teret), cash (prošlo kroz ruke restorana), info (korekcija), ink (isplata).
 */
export const groupTransactions = (rows) => {
  const map = new Map();
  rows.forEach((row) => {
    let key;
    if (row.sourceType === "ORDER" && row.sourceId !== null) key = `order:${row.sourceId}`;
    else if (row.transactionId !== null) key = `t:${row.transactionId}`;
    else key = `${row.sourceType}:${row.sourceId}:${row.id}`;
    if (!map.has(key)) {
      const isOrder = key.startsWith("order:");
      map.set(key, { key, isOrder, sourceType: isOrder ? "ORDER" : row.sourceType, sourceId: isOrder ? String(row.sourceId) : row.sourceId, date: row.date, rows: [] });
    }
    const group = map.get(key);
    group.rows.push(row);
    if (row.date && (!group.date || row.date < group.date)) group.date = row.date;
  });
  return [...map.values()].map((group) => {
    const lines = group.rows.map((row) => ({
      id: row.id,
      entryType: row.entryType,
      rateType: row.rateType,
      rateValue: row.rateValue,
      signed: row.direction === "DEBIT" ? -row.amount : row.amount,
      isCash: isCash(row.entryType),
      isRevenue: row.entryType === "ORDER_REVENUE",
    }));
    const net = r2(lines.reduce((sum, line) => sum + line.signed, 0));
    const hasCash = lines.some((line) => line.isCash);
    const advance = group.rows.some((row) => row.entryType === "CASH_ADVANCE");
    const hasRefund = group.rows.some((row) => isRefund(row.entryType));
    const method = (group.rows.find((row) => row.paymentMethod) || {}).paymentMethod || "";
    const service = (group.rows.find((row) => row.serviceName) || {}).serviceName || "";
    const event = EVENT_SOURCE_META[group.sourceType] || null;
    let tone = "cash";
    if (group.isOrder) tone = hasRefund ? "info" : hasCash ? "cash" : "in";
    else if (event && event.color === "#1d1d1f") tone = "ink";
    else if (net > 0) tone = "in";
    else if (net < 0) tone = "out";
    return {
      ...group,
      lines,
      net,
      hasCash,
      advance,
      hasRefund,
      paymentMethod: method,
      service,
      note: (group.rows.find((row) => row.note) || {}).note || "",
      eventTitleKey: event ? event.labelKey : "",
      eventCaptionKey: event ? event.captionKey : "",
      tone,
    };
  });
};

/** Zbir iznad liste iz totals (apsolutni iznosi); null kad backend nije poslao totals. */
export const summaryFromTotals = (totals, currency) =>
  totals
    ? {
        currency,
        orders: toNumber(totals.order_count),
        turnover: Math.abs(toNumber(totals.turnover)),
        commission: Math.abs(toNumber(totals.commission)),
        cash: Math.abs(toNumber(totals.direct_cash)),
      }
    : null;

/**
 * Tipovi unosa koje nudimo u filteru "Tip stavke" (?entry_type=A,B). Backend prima samo ključeve iz
 * config('enum.ledger.entry_type_labels'); nepoznat ključ vraća 422 sa poljem `allowed`, pa se lista sama suzi (allowedFromError).
 * Lista je potvrđena odgovorom na `entry_type=NEPOZNATO` (30.10.2026): backend zna ovih 13 ključeva.
 */
export const ENTRY_TYPE_KEYS = [
  "ORDER_REVENUE",
  "COMMISSION",
  "CASH_ADVANCE",
  "CASH_RECEIVED",
  "DELIVERY_FEE",
  "COURIER_WAGE",
  "REFUND",
  "LOSS_COMPENSATION",
  "SUBSCRIPTION",
  "PAYOUT",
  "PAYMENT_RECEIVED",
  "REVERSAL",
  "WRITE_OFF",
];

/** Ključevi iz tijela 422 odgovora (`allowed` na vrhu ili u `errors`; lista ili mapa ključ -> naziv); null kad ga nema. */
export const allowedFromError = (body) => {
  const source = body || {};
  const errors = source.errors && typeof source.errors === "object" ? source.errors : {};
  const found = [source.allowed, errors.allowed, errors.entry_type && errors.entry_type.allowed, source.data && source.data.allowed].find((value) => value && typeof value === "object");
  if (!found) return null;
  const keys = Array.isArray(found) ? found : Object.keys(found);
  const list = keys.map((key) => String(key)).filter(Boolean);
  return list.length ? list : null;
};

/** Izbor tipova -> vrijednost parametra: sve izabrano (ili ništa) znači bez filtera (undefined). */
export const entryTypeParam = (selected, offered) => {
  const chosen = (selected || []).filter((key) => offered.includes(key));
  return chosen.length && chosen.length < offered.length ? chosen.join(",") : undefined;
};

// ------------------------------------------------------------------ narudžbe koje čekaju knjiženje

const unwrap = (body) => {
  const source = body || {};
  return source.data && !Array.isArray(source.data) && typeof source.data === "object" && !source.meta ? source.data : source;
};

const textOrEmpty = (value) => (value === null || value === undefined ? "" : String(value));

/** Red `ledger/pending` i `admin/ledger/missing`: završena narudžba kojoj nedostaje knjiženje. */
export const mapPendingRow = (dto) => {
  const restaurant = dto.restaurant && typeof dto.restaurant === "object" ? dto.restaurant : {};
  const courier = dto.courier && typeof dto.courier === "object" ? dto.courier : {};
  return {
    key: String(dto.order_id !== undefined ? dto.order_id : dto.id),
    orderId: dto.order_id !== undefined ? dto.order_id : dto.id,
    date: textOrEmpty(dto.date || dto.created_at),
    state: dto.state === undefined ? null : dto.state,
    // delivered | failed (nepoznato ostaje kakvo je stiglo)
    outcome: textOrEmpty(dto.outcome).toLowerCase(),
    paymentMethod: textOrEmpty(dto.payment_method).toUpperCase(),
    value: dto.value === null || dto.value === undefined ? null : toNumber(dto.value),
    finalAt: textOrEmpty(dto.final_at),
    restaurantId: dto.restaurant_id !== undefined && dto.restaurant_id !== null ? dto.restaurant_id : restaurant.id !== undefined ? restaurant.id : null,
    restaurantName: textOrEmpty(dto.restaurant_name || restaurant.name || (typeof dto.restaurant === "string" ? dto.restaurant : "")),
    courier: textOrEmpty(dto.courier_name || courier.name || (typeof dto.courier === "string" ? dto.courier : "") || (dto.courier_id !== undefined && dto.courier_id !== null ? `#${dto.courier_id}` : "")),
    canPost: dto.can_post === undefined ? null : Boolean(dto.can_post),
    reason: textOrEmpty(dto.reason),
  };
};

const listOf = (source) => (Array.isArray(source) ? source : source.data || source.orders || source.rows || source.items || []);

/** GET /restaurants/{id}/ledger/pending: { data: [...], meta: { count, value_sum, note } } */
export const mapPendingList = (body) => {
  const source = unwrap(body);
  const meta = (!Array.isArray(source) && source.meta) || {};
  const rows = listOf(source).map(mapPendingRow);
  return {
    rows,
    count: meta.count !== undefined && meta.count !== null ? toNumber(meta.count) : rows.length,
    valueSum: meta.value_sum !== undefined && meta.value_sum !== null ? toNumber(meta.value_sum) : r2(rows.reduce((sum, row) => sum + (row.value || 0), 0)),
    note: textOrEmpty(meta.note),
  };
};

/** Najstariji `final_at` (ISO tekst) ili "" kad nijedan red nema datum. */
export const oldestFinalAt = (rows) => rows.map((row) => row.finalAt).filter(Boolean).sort()[0] || "";

// ------------------------------------------------------------------ izvod

/**
 * GET /balance-ledger: redovi onako kako ih backend šalje. Redoslijed i `balance` (tekuće stanje nad punim,
 * nefiltriranim nizom) su serverski i ne računaju se na klijentu; bez `balance` na redu stanje je null.
 */
export const ledgerRows = (entries) =>
  (entries || []).map((entry) => ({
    id: entry.id,
    date: entry.date || entry.created_at || "",
    description: entry.description || "",
    entryType: entry.entry_type || "",
    moneyFlow: entry.money_flow || null,
    sourceId: entry.source_id === undefined ? null : entry.source_id,
    amount: toNumber(entry.amount),
    balance: entry.balance !== null && entry.balance !== undefined ? toNumber(entry.balance) : null,
  }));

/** Tijelo odgovora /balance-ledger: sa omotačem {data: {...}} ili bez njega. */
export const ledgerPayload = (body) => {
  if (body && (body.ledger_entries !== undefined || body.opening_balance !== undefined)) return body;
  return (body && body.data) || {};
};

/** Brojevi stranica za paginator: prva, zadnja i susjedne od trenutne; null je praznina ("…"). */
export const pageWindow = (page, last) => {
  const set = new Set([1, last, page - 1, page, page + 1]);
  const pages = [...set].filter((p) => p >= 1 && p <= last).sort((a, b) => a - b);
  const out = [];
  pages.forEach((p, i) => {
    if (i && p - pages[i - 1] > 1) out.push(null);
    out.push(p);
  });
  return out;
};

/** Redovi razrade koje vraća GET /statement/lines (data.lines): iznos sa predznakom iz direction. */
export const mapStatementLine = (dto) => {
  const amount = Math.abs(toNumber(dto.amount));
  const orderId = dto.order_id !== undefined && dto.order_id !== null ? dto.order_id : dto.source_type === "ORDER" ? dto.source_id : null;
  return {
    id: dto.id,
    date: dto.date || dto.created_at || "",
    label: dto.label || dto.description || dto.note || "",
    orderId: orderId || null,
    baseAmount: dto.base_amount !== undefined && dto.base_amount !== null ? toNumber(dto.base_amount) : null,
    rateType: dto.rate_type === undefined ? null : dto.rate_type,
    rateValue: dto.rate_value !== undefined && dto.rate_value !== null ? Number(dto.rate_value) : null,
    amount: dto.direction === "DEBIT" ? -amount : amount,
  };
};

/** GET /restaurants/{id}/orders/{orderId}/summary */
export const mapOrderSummary = (data) => {
  const source = data || {};
  const customer = source.customer && typeof source.customer === "object" ? source.customer : { name: source.customer_name || source.customer || "" };
  return {
    orderId: source.order_id,
    date: source.date || "",
    customerName: customer.name || "",
    customerPhone: customer.phone || "",
    orderValue: toNumber(source.order_value),
    deliveryPrice: source.delivery_price !== undefined && source.delivery_price !== null ? toNumber(source.delivery_price) : null,
    paymentMethod: String(source.payment_method || "").toUpperCase(),
    alreadyRefunded: toNumber(source.already_refunded),
    maxRefund: source.max_refund !== undefined && source.max_refund !== null ? toNumber(source.max_refund) : null,
  };
};
